import React, { useState } from 'react';
import styled from 'styled-components';
import { useServiceVideos } from '../../services/hooks/useCatalog';

const INITIAL_COUNT = 4;

const Section = styled.section`
  max-width: 1100px;
  margin: 0 auto;
  padding: 26px 0 8px;
`;

const Head = styled.div`
  padding: 0 20px 16px;
`;

const H2 = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.4px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  padding: 0 20px 16px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (min-width: 992px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  overflow: hidden;
  transition: transform 0.22s cubic-bezier(0.16,1,0.3,1), box-shadow 0.22s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 30px rgba(0,0,0,0.1);
  }
`;

const Video = styled.video`
  width: 100%;
  aspect-ratio: 9 / 16;   /* Instagram reel portrait ratio */
  object-fit: cover;
  display: block;
  background: #f3f4f6;
`;

const Label = styled.p`
  margin: 0;
  padding: 10px 12px 12px;
  font-size: 13.5px;
  font-weight: 700;
  color: #111827;
  line-height: 1.35;
`;

const MoreWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 6px 20px 20px;
`;

const MoreBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1.5px solid #082189;
  background: #ffffff;
  color: #082189;
  font-size: 14px;
  font-weight: 800;
  padding: 11px 28px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.16s, color 0.16s, transform 0.12s;

  &:hover {
    background: #082189;
    color: #ffffff;
    transform: translateY(-1px);
  }
`;

const ServiceVideosSection = () => {
  const { data } = useServiceVideos();
  const [expanded, setExpanded] = useState(false);

  const allVideos = Array.isArray(data?.[0]?.celebrationVideos)
    ? data[0].celebrationVideos
    : [];

  if (allVideos.length === 0) return null;

  const videos = expanded ? allVideos : allVideos.slice(0, INITIAL_COUNT);
  const hasMore = allVideos.length > INITIAL_COUNT;

  return (
    <Section>
      <Head>
        <H2>See Our Work</H2>
      </Head>
      <Grid>
        {videos.map((v, i) => (
          <Card key={v?.id ?? i}>
            <Video
              src={v?.videoUrl}
              poster={v?.imgUrl}
              muted
              loop
              playsInline
              autoPlay
              preload="metadata"
            />
            {v?.name ? <Label>{v.name}</Label> : null}
          </Card>
        ))}
      </Grid>

      {/* Only render the button when there is actually more to reveal —
          avoids an empty CTA sitting over blank space. */}
      {hasMore && !expanded ? (
        <MoreWrap>
          <MoreBtn type="button" onClick={() => setExpanded(true)}>
            See More →
          </MoreBtn>
        </MoreWrap>
      ) : null}
    </Section>
  );
};

export default ServiceVideosSection;
